import React, { useEffect, useState } from "react";
import { Share, Plus, X } from "lucide-react";
import { logDev } from "@/lib/log";

const DISMISS_KEY = "ts_ios_hint_dismissed_v1";

function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // Include iPadOS 13+ which spoofs Mac but has touch support.
  const iOSLike = /iPad|iPhone|iPod/.test(ua)
    || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
  return iOSLike;
}

function isInStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS-specific standalone flag.
  return !!window.navigator.standalone;
}

/**
 * One-time iOS "Add to Home Screen" hint.
 * iOS Safari does NOT fire `beforeinstallprompt`, so users must manually add
 * the site via the Share sheet. This banner walks them through it — shown
 * only on iOS Safari (not Chrome iOS / not already-installed PWA) and
 * dismissible for the whole browser lifetime.
 */
export default function IosInstallHint() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isIos() || isInStandalone()) return;
    let dismissed = false;
    try { dismissed = localStorage.getItem(DISMISS_KEY) === "1"; } catch (err) { logDev(err); }
    if (dismissed) return;
    // Small delay so it doesn't compete with the initial page paint.
    const t = setTimeout(() => setShow(true), 1800);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setShow(false);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch (err) { logDev(err); }
  };

  if (!show) return null;

  return (
    <div
      data-testid="ios-install-hint"
      className="fixed z-40 left-4 right-4 bottom-24 sm:bottom-6 sm:right-6 sm:left-auto sm:w-[360px] rounded-2xl border border-slate-800 bg-slate-900 text-white p-4 shadow-[0_20px_50px_-10px_rgba(0,0,0,0.5)]"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#00b4ff]/15 grid place-items-center text-[#00b4ff] shrink-0">
          <Plus size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-sm tracking-tight">Install on iPhone</div>
          <p className="mt-1 text-xs text-slate-300 leading-relaxed">
            Tap <span className="inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded bg-slate-800 text-[#00b4ff]"><Share size={11} /> Share</span> in Safari, then choose <span className="text-white font-semibold">&ldquo;Add to Home Screen&rdquo;</span> untuk install Trading Secret sebagai app.
          </p>
          <button
            data-testid="ios-install-dismiss"
            onClick={dismiss}
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono-plex tracking-widest uppercase px-4 py-2 rounded-full border border-slate-700 hover:bg-slate-800 text-slate-300"
            style={{ transition: "background-color 0.2s ease" }}
          >
            Got it
          </button>
        </div>
        <button
          onClick={dismiss}
          aria-label="Close"
          className="text-slate-400 hover:text-white p-1 -mr-1 -mt-1"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
